import { useInfiniteQuery, useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import dayjs from 'dayjs';
import startCase from 'lodash/startCase';
import qs from 'qs';
import * as Sentry from '@sentry/react';

import {
  CASE_UPSERT_ENDPOINT,
  CASES_SEARCH_ENDPOINT,
  CASES_SEARCH_FILTER_PARAMS_ENDPOINT,
  CASES_V2_ENDPOINT,
  CASES_V2_EXISTING_CASE_ENDPOINT,
  CASES_V2_NEWS_CASE_ENDPOINT,
  datahubAxiosClient,
  ENDPOINT_DETAILS,
  REMOVE_BATCH_MATERIAL_ENDPOINT,
} from 'constants/server/apiEndPoint';
import { ApiGetListResponse, ApiMutationFn } from 'types/apiParamsTypes';
import { SortDirection } from 'constants/server/apiParams';
import {
  ICaseDetailsRequestParams,
  IDatahubCase,
  IGetCasesListParams,
  ITextUpdateResponse,
  IUpdateCaseAddressParams,
  LinkType,
} from './models';
import { mapCaseDetailsRequestParamsToDto, mapCaseFiltersFromDto, mapDatahubCaseFromDto } from './helpers';
import { mapSimplifiedVisualMaterialFromDto } from 'api/material/helpers';
import { ICaseFiltersDto, IDatahubCaseDto } from './dtos';
import { IAllowedMaterialNameDto, IBatchItemDto } from 'api/material/dtos';
import { mapSimplifiedOrderFromDto } from 'api/orders/helpers';
import { ISimplifiedOrder, OrderStatus } from 'api/orders/models';
import { IAddressDto } from 'api/address/dtos';
import notifyHandler from 'utils/notifyHandler';
import { useLocalization } from 'localization';
import { IContactPerson } from 'api/users/models';
import { ICommonResponse } from 'api/types';
import { groupTextsFromDto, initializeTextGroups } from 'api/texts/helpers';
import { ICaseTextGroup } from 'api/texts/models';
import { STATUS_LABEL, StatusesType } from 'constants/enums/status';

export type CaseAssets = {
  batchList: IBatchItemDto[];
  links: {
    linkType: LinkType.InteractiveFloorPlanEditor | LinkType.HotSpotEditor | LinkType.HotSpotZip;
    url: string;
  }[];
  materials: (ReturnType<typeof mapSimplifiedVisualMaterialFromDto> & { links?: { name: string; url: string }[] })[];
};

export type CaseCollagePrice = {
  featureEnabled: boolean;
  price: number;
  currency: string;
};

export type CopyAssetParams = {
  assetIds: number[];
  branchId: string;
  copyAll: boolean;
  copyWriteTexts: boolean;
  copyDocuments: boolean;
  copyLinks: boolean;
  reference: string;
};

export type UpdateAssetsParams = {
  assetId: string;
  assetName: string;
  orderPosition: number;
  tags?: string[];
};

export type CasesSearchResponse = {
  total: number;
  results: IDatahubCaseDto[];
};

export type CasesPayload = Partial<{
  searchCasesPayload: IGetCasesListParams;
  enableQuery: boolean;
  caseId: string;
  customerId: string | null;
  email: string;
  reference: string | null;
  content: string;
  fieldName: string;
  collageUrl: string;
  branchId: string;
  address: IAddressDto;
  orderContact?: IContactPerson[];
  searchTerm: string;
  skip: number;
  take: number;
  taskStatuses: string;
}>;

export type TextDocumentField = {
  fieldName: string;
  value: string;
};

export type UpsertTextDocumentsPayload = {
  fields: TextDocumentField[];
  approvedBy?: string;
};

export type TextContents = {
  name: string;
  title: string;
  content: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string | null;
};

export type TextGroups = { groupName: string; texts: TextContents[] }[];

export type TextHistoryResponse = {
  textSectionsHistory: TextContents[];
} & Pick<CasesPayload, 'caseId' | 'customerId' | 'reference'>;

export type OpenTasksResponse = Omit<IDatahubCaseDto, 'country' | 'contactPerson'> & {
  foreignSyatemReference: string | null;
};

type CaseQueryPayload = Partial<{
  caseId: string;
  caseAddress: IUpdateCaseAddressParams;
}> &
  ApiMutationFn;

export type StatisticStatus = {
  [key in StatusesType as Capitalize<key>]: number;
};

export type CountStatus = {
  statistic: StatisticStatus;
};

export type CurrentChainData = {
  id: string;
  organizationId: string;
  name: string;
  phoneNr: string | null;
  externalId: string | null;
  webUrl: string | null;
};

export const casesQueryKey = {
  all: ['cases'] as const,
  asset: ['caseAssets'] as const,
  latestCases: () => [...casesQueryKey.all, 'latestCases'] as const,
  assets: (caseId: string) => [...casesQueryKey.asset, caseId] as const,
  collagePrice: (caseId: string) => [caseId, 'collagePrice'] as const,
  case: (caseId: string) => [caseId, 'case'] as const,
  filters: (caseId: string) => [caseId, 'caseFilters'] as const,
  ordersInCase: (caseId: string) => [caseId, 'ordersInCase'] as const,
  detail: (caseId: string) => [caseId, 'caseDetail'] as const,
  assetOptions: (caseId: string) => [caseId, 'assetOptions'] as const,
  searchCases: (payload: IGetCasesListParams) => [...casesQueryKey.all, 'searchCases', payload] as const,
  getTextGroups: (caseId: string) => [caseId, 'getTextGroups'] as const,
  getTextSection: (caseId: string) => [caseId, 'getTextSection'] as const,
  getTextData: (caseId: string) => [caseId, 'getTextData'] as const,
  getNewCases: (searchTerm: string, skip: number, take: number) => [searchTerm, take, 'getNewCases'] as const,
  getExistingCases: (searchTerm: string, skip: number, take: number) =>
    [searchTerm, skip, take, 'getExistingCases'] as const,
  getOpenTasksByCases: (status: string, take: number) => [...casesQueryKey.all, status, take, 'openCases'] as const,
  filterParams: () => [...casesQueryKey.all, 'filterParams'] as const,
  countCases: () => [...casesQueryKey.all, 'countCases'] as const,
  currentChain: () => [...casesQueryKey.all, 'chain'] as const,
};

export const getLatestCaseApi = () => {
  const { data, isLoading, isFetching, isError } = useQuery<IDatahubCaseDto[]>({
    queryKey: casesQueryKey.latestCases(),
    queryFn: async () => {
      const response = await datahubAxiosClient.post(CASES_SEARCH_ENDPOINT, {
        skip: 0,
        take: 100,
        sortOrder: 'ModifiedDate',
        sortDirection: SortDirection.Desc,
        orderStatuses: [STATUS_LABEL.delivered],
        mustHaveOrder: true,
      });
      return response?.data?.results;
    },
  });

  return { data, isLoading, isFetching, isError };
};

export const upsertCaseMutation = (payload: ICaseDetailsRequestParams) => {
  const transformedPayload = mapCaseDetailsRequestParamsToDto(payload);
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async () => {
      const response = datahubAxiosClient.post<{ caseId: string }>(CASE_UPSERT_ENDPOINT, transformedPayload);
      return response;
    },
    onSuccess: response => {
      Sentry.addBreadcrumb({
        message: 'Upsert case successful',
        level: 'info',
        data: {
          payload: transformedPayload,
          response: response,
        },
      });

      Sentry.captureMessage('Case upserted successfully', 'info');
    },
    onError: error => {
      Sentry.addBreadcrumb({
        message: 'Upsert case failed',
        level: 'error',
        data: {
          payload: transformedPayload,
          error: error,
        },
      });

      Sentry.captureException(error, {
        tags: {
          operation: 'upsert_case',
        },
        contexts: {
          payload: transformedPayload as any,
        },
      });
    },
  });

  return { mutateAsync, isLoading, data, isSuccess };
};

export const getCaseAssetsApi = (caseId: string) => {
  return useQuery<CaseAssets>({
    queryKey: casesQueryKey.assets(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getAssets(caseId), {
        signal,
      });

      return {
        batchList: response?.data?.batchProductInfos ?? [],
        links: response?.data?.links ?? [],
        materials: (response?.data?.materials ?? []).map(mapSimplifiedVisualMaterialFromDto),
      };
    },
    enabled: !!caseId,
    select(data) {
      // filtering
      return {
        ...data,
        materials: data.materials.slice().sort((a, b) => {
          // Take the listOrder from each originalMaterialAsset If It has, If not assign max value to remain the initial value
          const orderA = a?.sortOrder ?? Number.MAX_VALUE;
          const orderB = b?.sortOrder ?? Number.MAX_VALUE;

          //If one of each value has listOrder = 0 then place it at the end
          if (orderA === 0 && orderB !== 0) {
            return 1;
          }
          if (orderB === 0 && orderA !== 0) {
            return -1;
          }
          return orderA - orderB;
        }),
      };
    },
  });
};

export const getCollagePriceByCaseIdApi = (caseId: string) => {
  return useQuery<CaseCollagePrice>({
    queryKey: casesQueryKey.collagePrice(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getCollagePrice(caseId), {
        signal,
      });
      return response.data;
    },
    enabled: !!caseId,
  });
};

export const getCaseApi = (caseId: string) => {
  const { data, isLoading } = useQuery<IDatahubCaseDto>({
    queryKey: casesQueryKey.case(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getCase(caseId), {
        signal,
      });
      return response.data;
    },
    enabled: !!caseId,
  });

  return { data, isLoading };
};

export const getCaseDetailApi = (caseId: string) => {
  return useQuery({
    queryKey: casesQueryKey.detail(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.caseDetail(caseId), {
        signal,
      });
      return response.data ? mapDatahubCaseFromDto(response.data) : undefined;
    },
    enabled: !!caseId,
  });
};

export const getOrdersInCaseApi = (caseId: string) => {
  return useQuery<ISimplifiedOrder[]>({
    queryKey: casesQueryKey.ordersInCase(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.ordersInCase(caseId), {
        signal,
      });
      return response.data.map(mapSimplifiedOrderFromDto);
    },
    enabled: !!caseId,
  });
};

export const updateCaseMutation = () => {
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (payload: CasesPayload) => {
      const response = datahubAxiosClient.post<{ caseId: string }>(CASE_UPSERT_ENDPOINT, { ...payload });
      return response;
    },
    onError: () => {},
    onSuccess: () => {},
  });

  return { mutateAsync, isLoading, data, isSuccess };
};

export const caseCopyAssetsMutation = (caseId: string) => {
  const { t } = useLocalization();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (payload: CopyAssetParams) => {
      const response = datahubAxiosClient.post<{ caseId: string }>(ENDPOINT_DETAILS.copyAssets(caseId), {
        ...payload,
      });
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.copyMaterial.error') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
    },
  });

  return { mutateAsync, isLoading, data, isSuccess };
};

export const getCaseAssetOptionsApi = (caseId: string) => {
  return useQuery<IAllowedMaterialNameDto[]>({
    queryKey: casesQueryKey.assetOptions(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getAssetOptions(caseId), {
        signal,
      });
      return response.data;
    },
    enabled: !!caseId,
  });
};

export const updateCaseAssetsMutation = (caseId: string) => {
  const { t } = useLocalization();
  const query = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateAssetsParams[]) => {
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.updateAssets(caseId), {
        assets: payload,
      });
      return response.data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('orders.pages.new.createOrderFailedDueToUnknownError') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      query.invalidateQueries({ queryKey: casesQueryKey.assets(caseId) });
    },
  });
};

export const recreateMaterialsMutation = (caseId: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async () => {
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.recreateMaterials(caseId));
      return response.data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.uploader.failed') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      queryClient.invalidateQueries({ queryKey: casesQueryKey.assets(caseId) });
    },
  });

  return { mutateAsync, isLoading, data, isSuccess };
};

export const removeBatchMaterialsMutation = () => {
  const { t } = useLocalization();

  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (orderLineId: string[]) => {
      const response = await datahubAxiosClient.delete(REMOVE_BATCH_MATERIAL_ENDPOINT, {
        data: orderLineId,
      });
      return response.data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('orders.pages.new.createOrderFailedDueToUnknownError') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
    },
  });

  return { mutateAsync, isLoading, data, isSuccess };
};

export const submitCollageMutation = () => {
  const { t } = useLocalization();

  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: CasesPayload) => {
      const { caseId = '', collageUrl } = params;
      const response = await datahubAxiosClient.post<ICommonResponse>(ENDPOINT_DETAILS.submitCollage(caseId), {
        collageUrl,
      });
      return response.data;
    },
    onError: () => {},
    onSuccess: response => {
      if (!response.success) {
        notifyHandler({ type: 'error', content: t('modals.collage.failed') });
        throw new Error(t('modals.collage.failed'));
      }
      notifyHandler({ type: 'success', content: t('modals.collage.success') });
    },
  });

  return { mutateAsync, isLoading, data, isSuccess };
};

export const getSearchCasesApi = (params: CasesPayload) => {
  const { searchCasesPayload = {}, enableQuery = false } = params;
  const {
    page = 0,
    pageSize = 100,
    departmentId,
    fromDate,
    toDate,
    query,
    category,
    orderBy,
    orderByDirection = 'descend',
    includeNoOrders,
    status,
  } = searchCasesPayload;

  const transformedPayload = {
    skip: page * pageSize,
    take: pageSize,
    branchIds: departmentId,
    modifiedDateFrom: fromDate && dayjs(fromDate).format('YYYY-MM-DD'),
    modifiedDateTo: toDate && dayjs(toDate).format('YYYY-MM-DD'),
    deliveredProductCategories: category,
    orderStatuses: status ? status.map(value => startCase(value.toLowerCase())) : undefined,
    searchKey: query,
    sortOrder: orderBy,
    sortDirection: orderByDirection && orderByDirection === 'descend' ? 'desc' : 'asc',
    mustHaveOrder: !includeNoOrders,
  };
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: casesQueryKey.searchCases(searchCasesPayload),
    queryFn: async () => {
      const response = await datahubAxiosClient.post<CasesSearchResponse>(CASES_SEARCH_ENDPOINT, transformedPayload);
      return {
        totalItems: response?.data?.total || 0,
        items: response?.data?.results.map(mapDatahubCaseFromDto) || [],
      };
    },
    enabled: enableQuery,
  });

  return { casesSearch: data, isSearchingCases: isLoading, isFetching, isError };
};

export const getTextSectionsApi = (caseId: string) => {
  return useQuery<ICaseTextGroup[]>({
    queryKey: casesQueryKey.getTextSection(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getTextSections(caseId), {
        signal,
      });
      return response?.data?.textSections ? groupTextsFromDto(response.data.textSections) : [];
    },

    enabled: !!caseId,
  });
};

export const getTextGroupsApi = (caseId: string) => {
  return useQuery<ICaseTextGroup[]>({
    queryKey: casesQueryKey.getTextGroups(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getTextGroups(), {
        signal,
      });
      return response?.data ? initializeTextGroups(response.data) : [];
    },

    enabled: !!caseId,
  });
};

export const textDocumentMutation = (caseId: string) => {
  const queryClient = useQueryClient();
  const { t } = useLocalization();
  return useMutation({
    mutationFn: async (options: {
      payload: UpsertTextDocumentsPayload;
      failedFieldHandler: (failedField: string) => void;
    }) => {
      const httpResponse = await datahubAxiosClient.post<ITextUpdateResponse>(
        ENDPOINT_DETAILS.createTextDocument(caseId),
        options.payload
      );

      const data = httpResponse.data;
      if (!data.success) {
        const failedFields = data.failedFields.map(field => field.name);
        if (!failedFields.length) {
          throw new Error('Unknown error due to failedFields property is empty');
        }
        for (const failedField of failedFields) {
          options?.failedFieldHandler(failedField);
        }
        notifyHandler({
          type: 'warning',
          content: t('modals.editText.partialError'),
        });
        return;
      }
      notifyHandler({ type: 'success', content: t('modals.editText.success') });
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.editText.error') });
      return;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: casesQueryKey.getTextSection(caseId) });
    },
  });
};

export const getTextDocumentHistoryApi = (params: CasesPayload) => {
  const { fieldName = '', caseId = '' } = params;
  return useQuery<TextHistoryResponse>({
    queryKey: casesQueryKey.getTextData(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getTextDocumentHistory(caseId, fieldName), {
        signal,
      });
      return response?.data;
    },
    staleTime: 0,
    enabled: !!caseId,
  });
};

export const updateCaseAddressMutation = (payload: CaseQueryPayload) => {
  const { caseId, handleSuccess } = payload;
  const queryClient = useQueryClient();
  const { t } = useLocalization();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: CaseQueryPayload['caseAddress']) => {
      const response = await datahubAxiosClient.post<ICommonResponse>(ENDPOINT_DETAILS.caseAddress(caseId ?? ''), {
        ...params,
      });
      return response.data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('cases.messages.addressUpdateFailed') });
    },
    onSuccess: res => {
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: casesQueryKey.detail(caseId ?? '') });
        notifyHandler({ type: 'success', content: t('cases.messages.addressUpdateSuccess') });
        handleSuccess?.();
      } else {
        notifyHandler({ type: 'error', content: t('cases.messages.addressUpdateFailed') });
      }
    },
  });

  return { updateCaseAddressAsync: mutateAsync, updatingAddress: isLoading, data, isSuccess };
};

export const getNewCasesApi = (params: CasesPayload) => {
  const { searchTerm = '', skip = 0, take = 5 } = params;
  const { data, isLoading, isFetching } = useQuery<IDatahubCase[]>({
    queryKey: casesQueryKey.getNewCases(searchTerm, skip, take),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(CASES_V2_NEWS_CASE_ENDPOINT, {
        params: {
          searchTerm,
          skip,
          take,
        },
      });
      return response?.data?.results.map(mapDatahubCaseFromDto) || [];
    },
    placeholderData: keepPreviousData,
  });
  return { newCases: data, loadingNewCases: isLoading, fetchingNewCases: isFetching };
};

export const getExistingCasesApi = (params: CasesPayload) => {
  const { searchTerm = '', skip = 0, take = 5 } = params;
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery<
    IDatahubCase[],
    Error
  >({
    queryKey: casesQueryKey.getExistingCases(searchTerm, skip, take),
    queryFn: async ({ pageParam }) => {
      const response = await datahubAxiosClient.get(CASES_V2_EXISTING_CASE_ENDPOINT, {
        params: { searchTerm, skip: pageParam, take },
      });
      const raw = response?.data?.results || [];
      return raw.map(mapDatahubCaseFromDto);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < take) {
        return undefined;
      }
      return allPages.length * take;
    },
    placeholderData: keepPreviousData,
  });

  return {
    existingCases: data?.pages?.flat() ?? [],
    loadingExistingCases: isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

export const getOpenTasksByCasesApi = (params: CasesPayload) => {
  const { take = 5, taskStatuses = OrderStatus.Open } = params;
  const { data, isLoading, isFetching, isError } = useQuery<OpenTasksResponse[]>({
    queryKey: casesQueryKey.getOpenTasksByCases(taskStatuses, take),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(CASES_V2_ENDPOINT, {
        params: { take, taskStatuses },
      });
      return response.data;
    },
  });

  return { data, isLoading, isFetching, isError };
};

export const getCaseFilterApi = () => {
  const { data } = useQuery({
    queryKey: casesQueryKey.filterParams(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get<ICaseFiltersDto>(CASES_SEARCH_FILTER_PARAMS_ENDPOINT);
      return response?.data && mapCaseFiltersFromDto(response.data);
    },
  });
  return { data };
};

export const countCasesApi = () => {
  const { data, isLoading, isFetching, isError, refetch } = useQuery<CountStatus>({
    queryKey: casesQueryKey.countCases(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getNumberOfCases(), {
        params: {
          statuses: [STATUS_LABEL.open, STATUS_LABEL.invoiced, STATUS_LABEL.delivered, STATUS_LABEL.cancelled],
        },
        paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
      });
      return response.data;
    },
    staleTime: 60000,
  });

  return { data, isLoading, isFetching, isError, refetch };
};

export const getChainNameApi = () => {
  const { data, isLoading, isFetching, isError, refetch } = useQuery<CurrentChainData>({
    queryKey: casesQueryKey.currentChain(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getCurrentChain());
      return response.data;
    },
  });

  return { data, isLoading, isFetching, isError, refetch };
};
